import { Box, Button, Flex, Text, useToast, VStack } from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useState } from 'react';
import { omit } from 'remeda';

import { funding as commonFunding } from '@finley-technologies/common';

import {
  AdjustmentSlider,
  AdjustmentSliderProps,
  Constraints,
} from './AdjustmentSlider';
import {
  getUnexpectedErrorMessage,
  dollarNumberWithCommas,
  numberWithCommas,
} from 'packages/common-frontend/utils';
import { FiArrowRight, FiFile } from 'react-icons/fi';
import { useCreateServicerReportPreviewMutation } from '~/borrower/domains/fundingRequests/requests/fundingRequests.gql.generated';
import { useFundingRequestContext } from '~/borrower/domains/fundingRequests/ui/manage/Context';
import {
  allocateFundingRequestTotalAmounts,
  updateCollectionsPaydownAmount,
  updateCollectionsRecycleAmount,
  updateCurePaydownAmount,
  updateDrawRequestAmount,
} from '~/borrower/domains/fundingRequests/ui/manage/Reducer';
import { FundingRequestSideColumn } from '~/borrower/domains/fundingRequests/ui/shared/ContainerSections';
import { FundingRequestInfoBox } from '~/borrower/domains/fundingRequests/ui/shared/InfoBox';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { colors } from '~/packages/common-frontend/colors';
import { PageHeader } from '~/packages/common-frontend/components/PageContainer';
import {
  errorToastConfig,
  errorToastId,
  successToastConfig,
} from '~/packages/common-frontend/constants';
import { useCreditFacilityContext } from '~/packages/credit-facility/hooks/CreditFacilityProvider';
import {
  useAllocateFundingRequestTargetAmountsMutation,
  useCanServicingReportPreviewBeCreatedQuery,
  useGetMaximumDrawRequestFrequencyMetadataQuery,
} from '~/packages/funding-requests/requests/requests.gql.generated';

const DEFAULT_PRECISION = 2;

const floorConstraints = (constraints: Constraints): Constraints => {
  return {
    max: _.floor(constraints.max, DEFAULT_PRECISION),
    min: _.floor(constraints.min, DEFAULT_PRECISION),
    step: constraints.step,
  };
};

const ceilConstraints = (constraints: Constraints): Constraints => {
  return {
    max: _.ceil(constraints.max, DEFAULT_PRECISION),
    min: _.ceil(constraints.min, DEFAULT_PRECISION),
    step: constraints.step,
  };
};

const toCurrencyDisplay = (val: number) => numberWithCommas(val) || 0;
const numberToCurrencyLabel = (val: number, inputType: string) =>
  `${inputType}: ${dollarNumberWithCommas(val)}`;

/**
 * Extend AdjustmentSlider with additional text label for FundingRequestAdjuster
 */
type FundingRequestAdjusterParams = AdjustmentSliderProps & {
  text: string;
  componentFrequencyMessage?: string;
  prefundedDrawContributionMessage?: string;
};

const FundingRequestAdjuster = ({
  text,
  componentFrequencyMessage,
  prefundedDrawContributionMessage,
  ...adjustmentSliderParams
}: FundingRequestAdjusterParams) => {
  return (
    <Flex padding="20px 0px" alignItems="center">
      <>
        <Text marginRight="30px" paddingTop="8px" fontSize="1em" w="150px">
          {text}
        </Text>
        <Text
          position="absolute"
          marginTop="100px"
          width="180px"
          color="red.400"
          fontSize="12px"
          lineHeight="base"
        >
          {componentFrequencyMessage}
        </Text>
      </>
      <Box flex="1">
        <AdjustmentSlider
          inputPosition="right"
          {...adjustmentSliderParams}
          showTooltip={Boolean(prefundedDrawContributionMessage)}
          tooltipLabel={prefundedDrawContributionMessage}
        />
      </Box>
    </Flex>
  );
};

/**
 * Main funding request plan pane
 */
const FundingRequestPlanMain = ({
  borrowerName,
  drawRequestLimitReached,
  drawRequestMessage,
  tabSelected,
}: {
  borrowerName: string;
  drawRequestLimitReached: boolean;
  drawRequestMessage: string | undefined;
  tabSelected: boolean;
}) => {
  const { dispatch, state } = useFundingRequestContext();
  const {
    componentTotalAmounts: {
      curePaydownTotalAmount,
      collectionsPaydownTotalAmount,
      collectionsRecycleTotalAmount,
      drawRequestTotalAmount,
    },
    constraints,
    _cashAdvanceRate,
    _adjustedPrincipalBalance,
    _totalMaxAdvanceAmount,
  } = state;

  const {
    isCollectionsRecycleEnabled,
    isVoluntaryPaydownAllowed,
    maxFundingRequestAmount,
  } = constraints._masterConstraints;

  const handleUpdateCurePaydownAmount = (val: number) => {
    dispatch(updateCurePaydownAmount(val));
  };

  const handleUpdateCollectionsPaydownAmount = (val: number) => {
    dispatch(updateCollectionsPaydownAmount(val));
  };

  const handleUpdateCollectionsRecycleAmount = (val: number) => {
    dispatch(updateCollectionsRecycleAmount(val));
  };

  const handleUpdateDrawRequestAmount = (val: number) => {
    dispatch(updateDrawRequestAmount(val));
  };

  const isDrawRequestVisible = maxFundingRequestAmount >= 0;
  const isCollectionsRecycleVisible =
    maxFundingRequestAmount >= 0 && isCollectionsRecycleEnabled === true;
  const isCollectionsPaydownVisible =
    maxFundingRequestAmount >= 0 && isVoluntaryPaydownAllowed === true;
  const isCurePaydownVisible = maxFundingRequestAmount < 0;

  // Calculation also reflected in Review.tsx component and funding-request controller generateFundingRequestNotificationsBaseParams
  // This equation becomes invalid if we allow simultaneous collections paydown and draw request
  const prefundedDrawContribution = commonFunding.getPrefundedDrawContribution({
    cashAdvanceRate: _cashAdvanceRate,
    collectionsRecycleTotalAmount,
    currentPrincipalBalance: _adjustedPrincipalBalance,
    drawRequestTotalAmount,
    totalMaxAdvanceAmount: _totalMaxAdvanceAmount,
  });

  let prefundedDrawContributionMessage;
  if (tabSelected && prefundedDrawContribution > 0) {
    prefundedDrawContributionMessage = `Prefunded draw contribution:\n$${toCurrencyDisplay(
      prefundedDrawContribution,
    )}`;
  }

  return (
    <VStack spacing={4} alignItems="left">
      <PageHeader headerLabel="Plan funding request" />
      <Box pb="20px">
        <Text color="#979797" size="sm" mb="8px">
          Recommended funding activity
        </Text>
        <Text fontSize="l" width="80%">
          Based on {borrowerName}&#39;s portfolio of assets, loan availability,
          and collections, we recommend the following funding request package.
          You can also use the sliders below to modify your funding request, but
          it&#39;s not necessary.
        </Text>
      </Box>
      <Box borderWidth="1px">
        <Box
          background={colors.tableHeader}
          w="100%"
          p={4}
          color="black"
          borderBottomWidth="1px"
        >
          Funding components
        </Box>
        {isCollectionsRecycleVisible && (
          <Box m={4}>
            <FundingRequestAdjuster
              text="Collections recycle"
              value={_.floor(collectionsRecycleTotalAmount, DEFAULT_PRECISION)}
              setValue={handleUpdateCollectionsRecycleAmount}
              constraints={floorConstraints(constraints.collectionsRecycle)}
              inputType="currency"
              inputDisplayCallback={toCurrencyDisplay}
              labelDisplayCallback={numberToCurrencyLabel}
            />
          </Box>
        )}
        {isDrawRequestVisible && (
          <Box m={4}>
            <FundingRequestAdjuster
              text="Credit facility draw"
              value={_.floor(drawRequestTotalAmount, DEFAULT_PRECISION)}
              setValue={handleUpdateDrawRequestAmount}
              constraints={floorConstraints(constraints.drawRequest)}
              inputType="currency"
              inputDisplayCallback={toCurrencyDisplay}
              labelDisplayCallback={numberToCurrencyLabel}
              componentFrequencyMessage={
                drawRequestLimitReached && drawRequestTotalAmount > 0
                  ? drawRequestMessage
                  : undefined
              }
              prefundedDrawContributionMessage={
                prefundedDrawContributionMessage
              }
            />
          </Box>
        )}
        {isCollectionsPaydownVisible && (
          <Box m={4}>
            <FundingRequestAdjuster
              text="Paydown recycle"
              value={_.floor(collectionsPaydownTotalAmount, DEFAULT_PRECISION)}
              setValue={handleUpdateCollectionsPaydownAmount}
              constraints={floorConstraints(constraints.collectionsPaydown)}
              inputType="currency"
              inputDisplayCallback={toCurrencyDisplay}
              labelDisplayCallback={numberToCurrencyLabel}
            />
          </Box>
        )}
        {isCurePaydownVisible && (
          <Box m={4}>
            <FundingRequestAdjuster
              text="Cure paydown"
              value={_.ceil(curePaydownTotalAmount, DEFAULT_PRECISION)}
              setValue={handleUpdateCurePaydownAmount}
              constraints={ceilConstraints(constraints.curePaydown)}
              inputType="currency"
              inputDisplayCallback={toCurrencyDisplay}
              labelDisplayCallback={numberToCurrencyLabel}
            />
          </Box>
        )}
      </Box>
    </VStack>
  );
};

export const FundingRequestPlanViewBody = ({
  continueFn,
  isActive,
}: {
  continueFn: () => void;
  isActive: boolean;
}) => {
  const toast = useToast();

  const { fundingRequestId, state, dispatch } = useFundingRequestContext();
  const { creditFacilityId } = useCreditFacilityContext();
  const [
    isContinueButtonLoading,
    setIsContinueButtonLoading,
  ] = useState<boolean>(false);
  const [isReportButtonLoading, setIsReportButtonLoading] = useState<boolean>(
    false,
  );

  const { components, componentTotalAmounts, creditAgreementId } = state;

  const {
    user: {
      organization: { name: borrowerName },
    },
  } = useUserAuthOrThrow();

  const {
    loading: canServicingReportPreviewBeCreatedLoading,
    data: canServicingReportPreviewBeCreatedData,
  } = useCanServicingReportPreviewBeCreatedQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
    },
  });

  const {
    loading: getMaximumDrawRequestFrequencyMetadataQueryLoading,
    data: getMaximumDrawRequestFrequencyMetadataQueryData,
  } = useGetMaximumDrawRequestFrequencyMetadataQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    // skip the initial query which occurs when state === fundingRequestInitialState (meaning the default
    // creditAgreementId of an empty string would be used)
    skip: !creditAgreementId,
    variables: { creditFacilityId, creditAgreementId },
  });

  const [
    allocateFundingRequestTotalAmountsMutation,
    { loading: allocationLoading },
  ] = useAllocateFundingRequestTargetAmountsMutation({
    variables: {
      fundingRequestTotalAmountsChangeset: {
        id: fundingRequestId,
        fundingRequestComponentTotalAmounts: componentTotalAmounts,
      },
    },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const {
        fundingRequestComponentTotalAmounts,
        fundingRequestComponents,
      } = data.allocateFundingRequestTargetAmounts;

      dispatch(
        allocateFundingRequestTotalAmounts({
          fundingRequestComponentTotalAmounts,
          fundingRequestComponents,
        }),
      );
    },
    onError: (err) => {
      if (!toast.isActive(errorToastId)) {
        errorToastConfig.title = `Error allocating funding request component total amounts`;
        toast(errorToastConfig);
      }
      throw err;
    },
  });

  const [
    createServicerReportPreview,
    { loading: createServicerReportPreviewLoading },
  ] = useCreateServicerReportPreviewMutation({
    variables: {
      fundingRequestChangeset: {
        id: fundingRequestId,
        fundingRequestComponents: _.flatten(_.values(components)),
      },
    },
    onCompleted: () => {
      successToastConfig.title =
        'Creating servicing report preview, please check your email in a few minutes.';
      toast(successToastConfig);
    },
    onError: () => {
      errorToastConfig.title = `Error creating servicing report preview.`;
      toast(errorToastConfig);
    },
  });

  const handlePreviewRequest = async () => {
    setIsReportButtonLoading(true);

    const { data } = await allocateFundingRequestTotalAmountsMutation();

    if (data) {
      const {
        fundingRequestComponents: allocatedFundingRequestComponents,
      } = data.allocateFundingRequestTargetAmounts;

      createServicerReportPreview({
        variables: {
          fundingRequestChangeset: {
            id: fundingRequestId,
            fundingRequestComponents: allocatedFundingRequestComponents.map(
              (frc) => omit(frc, ['__typename']),
            ),
          },
        },
      });
    } else {
      toast({
        position: 'top',
        status: 'error',
        isClosable: true,
        title: `Error creating servicing report preview.`,
      });
    }

    setIsReportButtonLoading(false);
  };

  if (
    canServicingReportPreviewBeCreatedLoading ||
    !canServicingReportPreviewBeCreatedData ||
    getMaximumDrawRequestFrequencyMetadataQueryLoading ||
    !getMaximumDrawRequestFrequencyMetadataQueryData
  ) {
    return <></>;
  }

  const {
    canCreate,
  } = canServicingReportPreviewBeCreatedData.canServicingReportPreviewBeCreated;

  const {
    limitReached,
    message,
  } = getMaximumDrawRequestFrequencyMetadataQueryData.maximumDrawRequestFrequencyMetadata;

  const ButtonRow = (
    <>
      <Button
        onClick={async () => {
          setIsContinueButtonLoading(true);
          await allocateFundingRequestTotalAmountsMutation();
          setIsContinueButtonLoading(false);
          continueFn();
        }}
        isLoading={isContinueButtonLoading && allocationLoading}
        rightIcon={<FiArrowRight />}
        backgroundColor="#3DCC91"
        color="white"
        _hover={{ bg: '#0F9960' }}
        size="lg"
        borderRadius="0"
      >
        Continue
      </Button>
      <Button
        isDisabled={!canCreate}
        mt="8"
        onClick={handlePreviewRequest}
        isLoading={
          isReportButtonLoading &&
          (allocationLoading || createServicerReportPreviewLoading)
        }
        rightIcon={<FiFile />}
        size="lg"
        borderRadius="0"
      >
        Create servicer report preview
      </Button>
    </>
  );
  return (
    <Flex justify="flex-start" width="auto" flexGrow={1}>
      <Box m={2} flex="5">
        <FundingRequestPlanMain
          borrowerName={borrowerName}
          drawRequestLimitReached={limitReached}
          drawRequestMessage={message || undefined}
          tabSelected={isActive}
        />
      </Box>
      <FundingRequestSideColumn
        ButtonRow={ButtonRow}
        FundingRequestInfoBox={<FundingRequestInfoBox />}
      />
    </Flex>
  );
};
